import { GQLNow } from "gql-now";
import {
  ORG_ID,
  MEMBER_SERVICES_URL,
  ADMIN_SERVICES_URL,
  ORG_SERVICES_URL,
  getAdminToken,
  handleUnauthorized,
} from "../js/auth.js";

// Ensure GQLNow supports mutate method alias
if (GQLNow?.prototype && !GQLNow.prototype.mutate && typeof GQLNow.prototype.mutation === "function") {
  GQLNow.prototype.mutate = GQLNow.prototype.mutation;
}

/**
 * Constructs standard headers for TRDR admin GraphQL requests.
 * Injects:
 *   - x-trdr-org
 *   - x-access-token
 *   - Authorization: Bearer <token>
 */
export function getAdminHeaders(customToken) {
  const token = customToken || getAdminToken() || "";
  const headers = {
    "x-trdr-org": ORG_ID,
  };
  if (token) {
    headers["x-access-token"] = token;
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Creates a GQLNow client for a given service URL with all auth headers
 */
export function createAdminClient(serviceUrl, customToken) {
  const headers = getAdminHeaders(customToken);
  return new GQLNow(serviceUrl, headers);
}

function handleGqlError(error) {
  if (!error) return;
  const status = error.status || error.statusCode;
  const msg = String(error.message || "").toLowerCase();
  if (status === 401 || msg.includes("unauthorized") || msg.includes("token expired") || msg.includes("jwt expired")) {
    const f7 = typeof window !== "undefined" && window.f7 ? window.f7 : null;
    handleUnauthorized(f7, null, "Your session has expired. Please log in again.");
  }
}

/**
 * Execute a GraphQL query on trdr-admin-services
 */
export async function adminQuery(operationName, variables = {}, selectionSet = "", customToken) {
  const client = createAdminClient(ADMIN_SERVICES_URL, customToken);
  try {
    return await client.query(operationName, variables, selectionSet);
  } catch (error) {
    handleGqlError(error);
    throw error;
  }
}

/**
 * Execute a GraphQL mutation on trdr-admin-services
 */
export async function adminMutation(operationName, variables = {}, selectionSet = "", customToken) {
  const client = createAdminClient(ADMIN_SERVICES_URL, customToken);
  try {
    return await client.mutate(operationName, variables, selectionSet);
  } catch (error) {
    handleGqlError(error);
    throw error;
  }
}

/**
 * Execute a GraphQL query on trdr-org-services
 */
export async function orgQuery(operationName, variables = {}, selectionSet = "", customToken) {
  const client = createAdminClient(ORG_SERVICES_URL, customToken);
  try {
    return await client.query(operationName, variables, selectionSet);
  } catch (error) {
    handleGqlError(error);
    throw error;
  }
}

/**
 * Execute a GraphQL mutation on trdr-org-services
 */
export async function orgMutation(operationName, variables = {}, selectionSet = "", customToken) {
  const client = createAdminClient(ORG_SERVICES_URL, customToken);
  try {
    return await client.mutate(operationName, variables, selectionSet);
  } catch (error) {
    handleGqlError(error);
    throw error;
  }
}

/**
 * Execute a GraphQL query on trdr-member-services
 */
export async function memberQuery(operationName, variables = {}, selectionSet = "", customToken) {
  const client = createAdminClient(MEMBER_SERVICES_URL, customToken);
  try {
    return await client.query(operationName, variables, selectionSet);
  } catch (error) {
    handleGqlError(error);
    throw error;
  }
}

/**
 * Execute a GraphQL mutation on trdr-member-services
 */
export async function memberMutation(operationName, variables = {}, selectionSet = "", customToken) {
  const client = createAdminClient(MEMBER_SERVICES_URL, customToken);
  try {
    return await client.mutate(operationName, variables, selectionSet);
  } catch (error) {
    handleGqlError(error);
    throw error;
  }
}
